import InfoIcon from '@mui/icons-material/Info'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import { Box, BoxProps, Button, Stack, Theme, Typography } from '@mui/material'
import { styled } from '@mui/material/styles'
import { useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'

import { useTypedSelector } from '../../'
import { EnergyEfficiencyStatistics as EnergyEfficiencyStatisticsType } from '../../types/Input'

const GOOD_DELTA_THRESHOLD = 0.02
const BAD_DELTA_THRESHOLD = 0.05

interface PercentageCircleProps extends BoxProps {
  deltaValue: number | null
}

const getCircleColor = (value: number | null, theme: Theme): string => {
  if (value === null) return theme.palette.energyEfficiency.disabled
  if (value <= GOOD_DELTA_THRESHOLD) return theme.palette.energyEfficiency.positive
  if (value > BAD_DELTA_THRESHOLD) return theme.palette.energyEfficiency.negative
  return theme.palette.energyEfficiency.neutral
}

const StyledPercentageCircle = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'deltaValue',
})<PercentageCircleProps>(({ deltaValue, theme }) => ({
  width: '60px',
  height: '60px',
  background: getCircleColor(deltaValue, theme),
  borderRadius: '50%',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  gap: '10px',
  display: 'inline-flex',
}))

// use en-US since fi-FI has a space between the sign and number
const PERCENTAGE_FORMAT = new Intl.NumberFormat('en-US', {
  signDisplay: 'exceptZero',
  style: 'percent',
  minimumFractionDigits: 1,
})

interface PercentageCircleComponentProps {
  value: number | null
  margin?: string
}

const PercentageCircle = ({ value, margin, ...props }: PercentageCircleComponentProps) => {
  return (
    <StyledPercentageCircle deltaValue={value} margin={margin} {...props}>
      <Typography fontSize="18px" fontWeight="500">
        {value != null ? PERCENTAGE_FORMAT.format(value) : '-%'}
      </Typography>
    </StyledPercentageCircle>
  )
}

const StyledFrame = styled(Box)<BoxProps>(() => ({
  paddingTop: '5px',
  paddingBottom: '5px',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  display: 'flex',
}))

const StyledEnergystatoverview = styled(Box)<BoxProps>(() => ({
  overflow: 'hidden',
  justifyContent: 'center',
  alignItems: 'center',
  display: 'flex',
}))

const InfoIconContainer = styled(Box)<BoxProps>(() => ({
  display: 'flex',
  flexGrow: 1,
  marginLeft: '10px',
  cursor: 'pointer',
}))

export const EnergyEfficiencyStatistics = ({
  statistics,
}: {
  statistics: EnergyEfficiencyStatisticsType
}) => {
  const { t } = useTranslation()
  const [showInfo, setShowInfo] = useState(false)
  const energyEfficiencyReportLink = useTypedSelector(
    (state) => state.configurations.data.energyEfficiencyReportLink
  )

  return (
    <Box
      sx={{
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'flex-start',
        display: 'inline-flex',
        width: '100%',
      }}
    >
      <Box
        display="inline-flex"
        alignItems="center"
        gap="10px"
        paddingTop="10px"
        paddingBottom="10px"
        width="100%"
      >
        <Typography variant="h6" color="grey.600">
          <Trans
            i18nKey={'energyEfficiency.compareYourUsage'}
            components={[<Box sx={{ display: 'inline', color: 'common.kenttaBlue' }} key="0" />]}
          />
        </Typography>
        <InfoIconContainer onClick={() => setShowInfo(!showInfo)}>
          <InfoIcon
            sx={{
              color: statistics.additionalInformation ? 'common.kenttaBlue' : 'action.disabled',
              fontSize: '1.2rem',
            }}
          />
        </InfoIconContainer>
      </Box>
      <Box
        sx={{
          alignSelf: 'stretch',
          justifyContent: 'space-between',
          alignItems: 'center',
          display: 'inline-flex',
        }}
      >
        <StyledEnergystatoverview gap="5px">
          <PercentageCircle value={statistics.consumptionRelativeToMedian8Weeks} margin="5px" />
          <StyledFrame>
            <Typography fontSize="12px" fontWeight="500" color="text.primary">
              {t('energyEfficiency.lastUnitPeriod', { quantity: 8, unit: t('weekAbbreviation') })}
            </Typography>
            <Typography fontSize="10px" fontWeight="400" color="text.primary">
              {t('energyEfficiency.trains')}: {statistics.trainCount8Weeks ?? '-'}
            </Typography>
          </StyledFrame>
        </StyledEnergystatoverview>
        <StyledEnergystatoverview gap="5px">
          <PercentageCircle value={statistics.consumptionRelativeToMedian365Days} />
          <StyledFrame>
            <Typography fontSize="12px" fontWeight="500" color="text.primary">
              {t('energyEfficiency.lastUnitPeriod', { quantity: 12, unit: t('monthAbbreviation') })}
            </Typography>
            <Typography fontSize="10px" fontWeight="400" color="text.primary">
              {t('energyEfficiency.trains')}: {statistics.trainCount365Days ?? '-'}
            </Typography>
          </StyledFrame>
        </StyledEnergystatoverview>
        {energyEfficiencyReportLink && (
          <Button
            href={energyEfficiencyReportLink}
            target="_blank"
            color="info"
            variant="text"
            sx={{
              maxWidth: '23%',
              textAlign: 'center',
            }}
          >
            <Stack>
              <Typography variant="button" textAlign="center">
                {t('energyEfficiency.showReport')}
                <OpenInNewIcon sx={{ fontSize: '1rem' }} />
              </Typography>
            </Stack>
          </Button>
        )}
      </Box>
      {showInfo && statistics.additionalInformation && (
        <Box sx={{ marginTop: '10px' }}>
          <Typography color="text.primary" variant="body2" sx={{ whiteSpace: 'pre-line' }}>
            {statistics.additionalInformation}
          </Typography>
        </Box>
      )}
    </Box>
  )
}
